import { useMemo } from 'react';
import {
  type ColumnDef,
  type FilterFn,
  type SortDirection,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, EllipsisVerticalIcon, SearchIcon } from 'lucide-react';
import { DateTime } from 'luxon';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useApi, useAsync, useNavigate } from '../../hooks';
import { AdminFrame, LoadFailed, Loading } from './adminFrame';

type Rendering = 'TEMPLATE' | 'STATIC_PDF';

export type Form = {
  id: string;
  name: string;
  code: string;
  formType: string;
  rendering: Rendering;
  productName: string;
  effectiveAt: number;
  // A form with no recorded end carries no key at all: the server omits null fields.
  effectiveUntil?: number | null;
  createdAt: number;
  updatedAt: number;
};

const DATE = 'MM-dd-yyyy';

const formatStamp = (millis: number): string => DateTime.fromMillis(millis).toFormat(DATE);

const formatFormType = (constant: string): string => {
  const phrase = constant.replace(/_/g, ' ').toLowerCase();
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
};

/* The code is searched though it is not shown, and the form type is searched both as the reader sees
   it and as it is stored, so a query typed either way finds it. */
const searchForms: FilterFn<Form> = (row, _columnId, query: string) => {
  const needle = query.trim().toLowerCase();
  const form = row.original;
  return [form.name, form.code, form.productName, form.formType, formatFormType(form.formType)].some((field) => field.toLowerCase().includes(needle));
};

const RowActions = ({ form }: { form: Form }) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
        <EllipsisVerticalIcon />
        <span className="sr-only">Actions for {form.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate.to(`/admin/forms/${form.id}/preview`)}>Preview</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const COLUMNS: ColumnDef<Form>[] = [
  { accessorKey: 'name', header: 'Form Name' },
  { id: 'formType', header: 'Form Type', accessorFn: (form) => formatFormType(form.formType) },
  { accessorKey: 'productName', header: 'Product' },
  { accessorKey: 'effectiveAt', header: 'Effective At', cell: ({ getValue }) => formatStamp(getValue<number>()) },
  /* The far-future sentinel a form can carry is a date it really holds, so it is shown like any other;
     only the absence of an end date reads as a dash, and sorts last whichever way the column runs. */
  {
    id: 'effectiveUntil',
    header: 'Effective Until',
    accessorFn: (form) => form.effectiveUntil ?? undefined,
    sortUndefined: 'last',
    cell: ({ getValue }) => {
      const until = getValue<number | undefined>();
      return until === undefined ? '--' : formatStamp(until);
    },
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    enableSorting: false,
    cell: ({ row }) => <RowActions form={row.original} />,
  },
];

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

const SortIcon = ({ direction }: { direction: SortDirection | false }) =>
  direction === 'asc' ? <ArrowUpIcon /> : direction === 'desc' ? <ArrowDownIcon /> : <ArrowUpDownIcon className="opacity-50" />;

export const FormsLibraryView = () => {
  const { getForms } = useApi();
  const { value, status } = useAsync(getForms, { immediate: true });

  const forms: Form[] = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const table = useReactTable({
    data: forms,
    columns: COLUMNS,
    globalFilterFn: searchForms,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Every column sorts A to Z, oldest first, on its first click, and a click only ever flips it.
    sortDescFirst: false,
    enableSortingRemoval: false,
    initialState: { sorting: [{ id: 'name', desc: false }] },
    /* The table is not paged, and the page reset TanStack otherwise queues after every search or sort
       is a state update for nothing. */
    autoResetPageIndex: false,
  });

  const query: string = table.getState().globalFilter ?? '';
  const rows = table.getRowModel().rows;

  return (
    <AdminFrame>
      <h1 className="text-2xl font-semibold">Forms Library</h1>
      {status === 'error' ? (
        <LoadFailed />
      ) : status === 'success' ? (
        forms.length ? (
          <div className="mt-4 w-full">
            <InputGroup className="max-w-sm">
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
              <InputGroupInput
                type="search"
                aria-label="Search forms"
                placeholder="Search forms…"
                value={query}
                onChange={(event) => table.setGlobalFilter(event.target.value)}
              />
            </InputGroup>
            <Table className="mt-3">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const { column } = header;
                      const label = flexRender(column.columnDef.header, header.getContext());
                      const direction = column.getIsSorted();
                      return column.getCanSort() ? (
                        <TableHead key={header.id} className="h-8" aria-sort={direction ? ARIA_SORT[direction] : 'none'}>
                          <Button variant="ghost" size="sm" className="-ml-2.5" onClick={column.getToggleSortingHandler()}>
                            {label}
                            <SortIcon direction={direction} />
                          </Button>
                        </TableHead>
                      ) : (
                        <TableHead key={header.id} className="h-8 w-10">
                          {label}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {rows.length ? (
                  rows.map((row) => (
                    <TableRow key={row.original.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className={cell.column.id === 'actions' ? 'py-1.5 text-right' : 'py-1.5'}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className="h-16 text-center text-muted-foreground">
                      No forms match “{query}”.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Empty className="mt-4">
            <EmptyHeader>
              <EmptyTitle>No forms were found.</EmptyTitle>
            </EmptyHeader>
          </Empty>
        )
      ) : (
        <Loading />
      )}
    </AdminFrame>
  );
};
